
import { useState, useEffect } from 'react';

export default function SystemStatusSection() {
  const [metrics, setMetrics] = useState({
    uptime: '99.9%',
    responseTime: '&lt;2min',
    resolutionTime: '4h',
    monitoring: '24/7',
    status: 'operational',
    statusColor: 'green',
    lastUpdated: null,
    ticketCount: 0,
    criticalIssues: 0,
    customerSatisfaction: '4.8/5',
    satisfactionPercentage: 96,
    resolvedTickets: 0,
    avgResolutionHours: 4
  });
  const [isLoading, setIsLoading] = useState(false);

  const fetchMetrics = async () => {
    setIsLoading(true);
    try {
      // Fetch real metrics from Freshservice via Supabase Edge Function
      const response = await fetch('https://txyawdaylusoolspukgs.supabase.co/functions/v1/freshservice-metrics');
      if (response.ok) {
        const data = await response.json();
        setMetrics(data);
      }
    } catch (error) {
      console.log('Using default metrics - Freshservice connection issue');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
    // Refresh metrics every 30 minutes (increased from 15 minutes)
    const interval = setInterval(fetchMetrics, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const getStatusDisplay = () => {
    switch (metrics.status) {
      case 'operational':
        return { text: 'All Systems Operational', color: 'green' };
      case 'degraded':
        return { text: 'Performance Issues', color: 'yellow' };
      case 'outage':
        return { text: 'Service Disruption', color: 'red' };
      default:
        return { text: 'All Systems Operational', color: 'green' };
    }
  };

  const statusDisplay = getStatusDisplay();

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Live System Status from Freshservice */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <h3 className="text-xl font-bold text-gray-900 mr-4">Live System Status</h3>
              {isLoading && (
                <i className="ri-loader-4-line animate-spin text-gray-500"></i>
              )}
            </div>
            <div className="flex items-center">
              <span className={`flex items-center font-medium ${
                statusDisplay.color === 'green' ? 'text-green-600' :
                statusDisplay.color === 'yellow' ? 'text-yellow-600' : 'text-red-600'
              }`}>
                <div className={`w-3 h-3 rounded-full mr-2 ${
                  statusDisplay.color === 'green' ? 'bg-green-500' :
                  statusDisplay.color === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'
                }`}></div>
                {statusDisplay.text}
              </span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className={`text-2xl font-bold mb-1 ${
                statusDisplay.color === 'green' ? 'text-green-600' :
                statusDisplay.color === 'yellow' ? 'text-yellow-600' : 'text-red-600'
              }`}>
                <div className={`w-4 h-4 rounded-full mx-auto mb-2 ${
                  statusDisplay.color === 'green' ? 'bg-green-500' :
                  statusDisplay.color === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'
                }`}></div>
                {metrics.status === 'operational' ? 'Online' : 
                 metrics.status === 'degraded' ? 'Issues' : 'Offline'}
              </div>
              <div className="text-sm text-gray-600">System Status</div>
              {metrics.criticalIssues > 0 ? (
                <div className="text-xs text-red-500 mt-1 font-medium">{metrics.criticalIssues} critical issues</div>
              ) : (
                <div className="text-xs text-green-600 mt-1 font-medium">All systems operational</div>
              )}
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600 mb-1">{metrics.uptime}</div>
              <div className="text-sm text-gray-600">System Uptime</div>
              <div className="text-xs text-green-600 mt-1 font-medium">This Month</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600 mb-1">{metrics.responseTime}</div>
              <div className="text-sm text-gray-600">Avg Response Time</div>
              {metrics.ticketCount > 0 && (
                <div className="text-xs text-blue-600 mt-1 font-medium">{metrics.ticketCount} tickets this month</div>
              )}
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600 mb-1">{metrics.resolutionTime}</div>
              <div className="text-sm text-gray-600">Avg Resolution Time</div>
              {metrics.resolvedTickets > 0 && (
                <div className="text-xs text-purple-600 mt-1 font-medium">{metrics.resolvedTickets} resolved tickets</div>
              )}
            </div>
          </div>

          {metrics.lastUpdated && (
            <div className="text-center mt-4 text-xs text-gray-500">
              Last updated: {new Date(metrics.lastUpdated).toLocaleString()} • Data from Freshservice API
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
